import React, { useEffect, useState, useCallback } from 'react';
import { Download, Share, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { api } from '../lib/api';

/**
 * PWAInstallButton
 *
 * Mobile-first "Add to Home Screen" affordance. It shows a small pill button
 * ("Instalar app") in the header when:
 *  1. The browser has fired `beforeinstallprompt` (Chrome/Edge on Android + desktop),
 *     OR
 *  2. The user is on iOS Safari (which doesn't fire the event but supports A2HS
 *     via the Share menu). For iOS we open an instructions dialog.
 *
 * Hidden after installation and if the app is already running standalone
 * (matchMedia('(display-mode: standalone)') OR navigator.standalone === true).
 * Users can dismiss it — dismissal is remembered in localStorage for 7 days.
 *
 * On install events, we POST /api/me/track-install so admins can see who
 * installed the app in the audit log (`/admin/audit-log`).
 */

const DISMISS_KEY = 'pc_a2hs_dismissed_at';
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
const TRACK_INSTALLED_KEY = 'pc_pwa_install_tracked_v1';
const TRACK_STANDALONE_KEY = 'pc_pwa_standalone_tracked_v1';

const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = (navigator.userAgent || navigator.vendor || '').toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  if (/win|mac|linux/.test(ua)) return 'desktop';
  return 'unknown';
};

const isStandaloneMode = () => {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari specific:
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
};

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const wasRecentlyDismissed = () => {
  try {
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return t > 0 && (Date.now() - t) < DISMISS_MS;
  } catch (_) { return false; }
};

const trackInstallOnce = async (event, storageKey) => {
  try {
    if (localStorage.getItem(storageKey)) return;
    await api.post('/me/track-install', {
      event,
      platform: detectPlatform(),
      user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
    });
    localStorage.setItem(storageKey, String(Date.now()));
  } catch (e) {
    // Best-effort telemetry — never surface to the user.
    console.warn('track-install failed', e);
  }
};

const PWAInstallButton = () => {
  const [deferred, setDeferred] = useState(null);          // BeforeInstallPromptEvent
  const [visible, setVisible] = useState(false);           // show pill button
  const [iosHelpOpen, setIosHelpOpen] = useState(false);   // iOS instructions dialog

  useEffect(() => {
    // If we detect the app is now running standalone AND we never tracked it,
    // log a one-time "standalone_first_open" event. This is our best signal
    // that an iOS user completed the "Add to Home Screen" flow.
    if (isStandaloneMode()) {
      trackInstallOnce('standalone_first_open', TRACK_STANDALONE_KEY);
      return; // already installed — no pill needed
    }
    if (wasRecentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      // Prevent Chrome's default mini-infobar; keep the event so we can trigger later.
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      trackInstallOnce('installed', TRACK_INSTALLED_KEY);
      toast.success('App instalada · abrila desde tu pantalla de inicio');
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // On iOS Safari, `beforeinstallprompt` never fires — show the button so
    // we can open manual instructions.
    if (isIOS()) setVisible(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice && choice.outcome === 'accepted') {
          toast.success('Instalando…');
          // 'appinstalled' event will fire soon and track the event.
        }
      } catch (e) {
        console.warn('install prompt failed', e);
      } finally {
        setDeferred(null);
        setVisible(false);
      }
      return;
    }
    if (isIOS()) {
      setIosHelpOpen(true);
      return;
    }
    // Fallback — no prompt available (Firefox/other): show a generic hint
    toast.info('Usá el menú del navegador → "Instalar app" o "Añadir a pantalla de inicio".');
  }, [deferred]);

  const dismiss = useCallback((e) => {
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <>
      <div className="inline-flex items-center gap-0.5 shrink-0" data-testid="pwa-install-wrapper">
        <button
          type="button"
          onClick={handleClick}
          className="inline-flex items-center gap-1 h-8 pl-2.5 pr-3 rounded-full bg-[#0a3ca5] hover:bg-[#082e80] text-white text-[11.5px] font-medium transition"
          title="Instalar la app en tu pantalla de inicio"
          data-testid="pwa-install-btn"
        >
          <Download size={13} strokeWidth={2}/>
          <span className="hidden xs:inline">Instalar</span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="w-6 h-8 inline-flex items-center justify-center text-[#8a8579] hover:text-[#1c1c1c] transition"
          title="Ocultar por 7 días"
          aria-label="Ocultar sugerencia de instalación"
          data-testid="pwa-install-dismiss"
        >
          <X size={12}/>
        </button>
      </div>

      <Dialog open={iosHelpOpen} onOpenChange={setIosHelpOpen}>
        <DialogContent className="max-w-sm bg-[#f8f6f1]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Instalar en iPhone / iPad</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[13px] text-[#3a3a3a]">
            <p className="text-[12.5px] text-[#5a5a5a]">
              iOS no muestra el instalador automático. Seguí estos pasos en Safari:
            </p>
            <ol className="space-y-2.5">
              <li className="flex gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#0a3ca5] text-white text-[11px] shrink-0">1</span>
                <span>Tocá el botón <Share size={13} className="inline align-text-bottom"/> <strong>Compartir</strong> en la barra inferior de Safari.</span>
              </li>
              <li className="flex gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#0a3ca5] text-white text-[11px] shrink-0">2</span>
                <span>Elegí <strong>&ldquo;Añadir a pantalla de inicio&rdquo;</strong> <Plus size={13} className="inline align-text-bottom"/> (bajá si es necesario).</span>
              </li>
              <li className="flex gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#0a3ca5] text-white text-[11px] shrink-0">3</span>
                <span>Confirmá el nombre &ldquo;PriceCapture&rdquo; y tocá <strong>Añadir</strong>.</span>
              </li>
            </ol>
            <p className="text-[11.5px] text-[#8a8579] pt-2 border-t border-[#e8e5de]">
              Una vez instalada, abrí PriceCapture desde el icono del teléfono para verla sin la barra de Safari.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PWAInstallButton;
